/* Generikus credential-panel egy asset-regiszter sorhoz (2026-09-05).
 *
 * A `GET /api/company-assets/{id}/credentials` mezőlistájából dolgozik: minden mezőnek
 * label / hint / secret / required / multiline / is_set / masked. Ugyanaz a három
 * javítás, mint a RaiffeisenCredentials.js-ben (Gábor 2026-08-11): a beállított mezőnél
 * NINCS input, csak lezárt sor + „Csere"; a piszkozat `sessionStorage`-ban él; a
 * PEM-szerű (multiline) mezőnél textarea.
 *
 * ⚠ Maszkolás (Gábor 2026-09-05-i döntése): a NEM titkos mezők (technikai user,
 * törzsszám, KID, client_id…) tisztán látszanak, a titkosak `első 4 ••• utolsó 4`
 * alakban (16 alatt csak az utolsó 4). A maszkot a BACKEND képzi — itt csak kiírjuk.
 *
 * A mentés ELŐTT friss, erre a kérésre kötött passkey (`requireFreshStepUp`, 2026-09-24 óta —
 * nem a 30 perces jegy), minden providernél: a NAV-kulcsokból az
 * utalás célszámlája, a Billingo-kulcsból a saját bankszámlaszám, a banki kulcsokból
 * maga az utalás jön. A Badge sosem vált „Aktív"-ra a mentéstől — csak a `/test`-től. */
import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, History, KeyRound, RotateCcw, X } from 'lucide-react';
import { companyAssetsApi, freshRequest, sendFresh } from '../services/api';
import { Alert, Badge, Btn } from './UI';
import { useToast } from './Toast';
import { useAuth } from '../contexts/AuthContext';
import { useStepUp } from '../contexts/StepUpContext';

const fmtTs = (iso) => iso
  ? new Date(iso).toLocaleString('hu-HU', { timeZone: 'Europe/Budapest',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—';

const LBL = { fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.06em' };
const BOX = { background: 'var(--bg-input)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', padding: '0.45rem 0.65rem',
              color: 'var(--text)', fontSize: '0.85rem', outline: 'none', width: '100%' };
const MONO = { fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem' };

function useDraft(key) {
  const [draft, setDraft] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(key) || '{}'); } catch { return {}; }
  });
  useEffect(() => {
    const any = Object.values(draft).some((v) => (v || '').trim() !== '');
    if (any) sessionStorage.setItem(key, JSON.stringify(draft));
    else sessionStorage.removeItem(key);
  }, [key, draft]);
  const clear = useCallback(() => { setDraft({}); sessionStorage.removeItem(key); }, [key]);
  return [draft, setDraft, clear];
}

/** Beállított mező: lezárt sor, maszkolt/tiszta értékkel, input NÉLKÜL. */
function SetRow({ f, onReplace, canEdit }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  padding: '0.45rem 0.65rem', background: 'rgba(34,197,94,0.06)',
                  border: '1px solid rgba(34,197,94,0.25)', borderRadius: 'var(--radius)' }}>
      <Check size={15} style={{ color: '#22c55e', flexShrink: 0 }}/>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{f.label}</div>
        {f.masked && (
          <div style={{ ...MONO, color: f.secret ? 'var(--text-muted)' : 'var(--text)', marginTop: 2,
                        wordBreak: 'break-all' }}
               title={f.secret ? 'Titkos mező — csak az eleje és a vége látszik' : undefined}>
            {f.masked}
          </div>
        )}
        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
          beállítva: {fmtTs(f.updated_at)}
          {f.version_label ? ` · ${f.version_label}` : ''}
          {f.history_count > 0 ? ` · ${f.history_count} korábbi verzió` : ''}
        </div>
      </div>
      <Btn size="sm" variant="secondary" disabled={!canEdit}
           title={canEdit ? undefined : 'Csak cég-adminisztrátor módosíthatja.'}
           onClick={() => onReplace(f.field)}>
        <RotateCcw size={13}/> Csere
      </Btn>
    </div>
  );
}

function InputRow({ f, value, onChange, onCancelReplace, canEdit }) {
  const isReplace = !!f.is_set;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5,
                  ...(isReplace ? { padding: '0.6rem 0.7rem', background: 'rgba(245,158,11,0.06)',
                                    border: '1px solid rgba(245,158,11,0.35)', borderRadius: 'var(--radius)' } : {}) }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={LBL}>{f.label}</span>
        <Badge variant={f.required ? 'warning' : 'default'}>{f.required ? 'kötelező' : 'opcionális'}</Badge>
        {isReplace && (<>
          <Badge variant="pending">csere folyamatban</Badge>
          <Btn size="sm" variant="secondary" onClick={() => onCancelReplace(f.field)}>
            <X size={13}/> Mégse (marad a mostani)
          </Btn>
        </>)}
      </div>
      {isReplace && (
        <div style={{ fontSize: '0.75rem', color: '#fcd34d' }}>
          A mostani érték <b>érvényben marad</b> ({f.masked ? <span style={MONO}>{f.masked}</span> : fmtTs(f.updated_at)}) —
          csak akkor cserélődik, ha ide újat írsz <b>és mentesz</b>. A régi verzió a történetben megmarad.
        </div>
      )}
      {f.multiline ? (
        <textarea rows={4} spellCheck={false} value={value} autoComplete="off" disabled={!canEdit}
                  placeholder="-----BEGIN …-----"
                  onChange={(e) => onChange(f.field, e.target.value)}
                  style={{ ...BOX, ...MONO, fontSize: '0.72rem', resize: 'vertical' }}/>
      ) : (
        <input type={f.secret ? 'password' : 'text'} value={value} autoComplete="off" spellCheck={false}
               disabled={!canEdit} onChange={(e) => onChange(f.field, e.target.value)} style={BOX}/>
      )}
      {f.hint && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{f.hint}</span>}
    </div>
  );
}

/**
 * @param asset       a regiszter-sor (`id`, `asset_type`, `provider_label`)
 * @param stepUpWhy   a step-up modal előtagja, pl. 'A Billingo-kapcsolat módosítása előtt '
 * @param whyText     miért kell step-up — a felhasználónak kiírva a mentés mellett
 */
export default function AssetCredentialsPanel({ asset, stepUpWhy, whyText, title }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { isAdmin } = useAuth();
  const { requireFreshStepUp } = useStepUp();
  const [replacing, setReplacing] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [label, setLabel] = useState('');
  const [draft, setDraft, clearDraft] = useDraft(`asset-cred-draft:${asset.id}`);

  const { data: st, isLoading } = useQuery({
    queryKey: ['company-asset-credentials', asset.id],
    queryFn: () => companyAssetsApi.credentials(asset.id),
  });
  const { data: hist } = useQuery({
    queryKey: ['company-asset-cred-history', asset.id],
    queryFn: () => companyAssetsApi.credentialHistory(asset.id),
    enabled: showHistory,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['company-asset-credentials', asset.id] });
    qc.invalidateQueries({ queryKey: ['company-asset-cred-history', asset.id] });
    qc.invalidateQueries({ queryKey: ['company-assets', asset.asset_type] });
  };
  const filled = Object.entries(draft).filter(([, v]) => (v || '').trim() !== '');

  const saveMut = useMutation({
    // 2026-09-24: a kérés-leíró (`freshRequest`) megy — ugyanaz, amire a passkey szól.
    mutationFn: (req) => sendFresh(req),
    onSuccess: (d) => {
      clearDraft(); setReplacing({}); setLabel('');
      const n = (d.written || []).length;
      toast.success(n ? `Mentve (${n} mező) — a kapcsolat élő tesztig „Teszt szükséges".`
                      : 'Nem változott semmi (a beírt érték azonos a tárolttal).');
      refresh();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'A mentés nem sikerült.'),
  });

  const onSave = async () => {
    if (!filled.length) { toast.info('Nincs kitöltött mező.'); return; }
    const req = freshRequest('POST /company-assets/{asset_id}/credentials', {
      path: { asset_id: asset.id },
      body: { credentials: Object.fromEntries(filled.map(([k, v]) => [k, v.trim()])),
              label: label.trim() || null } });
    if (!await requireFreshStepUp(stepUpWhy || 'A kapcsolat hitelesítő adatainak módosítása előtt ',
                                  [req])) return;
    saveMut.mutate(req);
  };
  const cancelReplace = (field) => {
    setReplacing((r) => { const n = { ...r }; delete n[field]; return n; });
    setDraft((d) => { const n = { ...d }; delete n[field]; return n; });
  };

  if (isLoading || !st) return null;
  const fields = st.fields || [];
  const missing = fields.filter((f) => !f.is_set);
  const inReplace = fields.filter((f) => f.is_set && replacing[f.field]);
  const done = fields.filter((f) => f.is_set && !replacing[f.field]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 6 }}>
      {title && <div style={LBL}>{title}</div>}
      {missing.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={LBL}>Kitöltendő ({missing.length})</div>
          {missing.map((f) => (
            <InputRow key={f.field} f={f} value={draft[f.field] || ''} canEdit={isAdmin}
                      onChange={(k, v) => setDraft((d) => ({ ...d, [k]: v }))} onCancelReplace={cancelReplace}/>
          ))}
        </div>
      )}
      {inReplace.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={LBL}>Csere folyamatban ({inReplace.length})</div>
          {inReplace.map((f) => (
            <InputRow key={f.field} f={f} value={draft[f.field] || ''} canEdit={isAdmin}
                      onChange={(k, v) => setDraft((d) => ({ ...d, [k]: v }))} onCancelReplace={cancelReplace}/>
          ))}
        </div>
      )}
      {done.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={LBL}>Beállítva ({done.length})</div>
          {done.map((f) => (
            <SetRow key={f.field} f={f} canEdit={isAdmin}
                    onReplace={(field) => setReplacing((r) => ({ ...r, [field]: true }))}/>
          ))}
        </div>
      )}
      {st.missing_required?.length > 0 && (
        <Alert type="error" style={{ marginBottom: 0 }}>
          Hiányzó <b>kötelező</b> mező: <b>{st.missing_required.map((f) => fields.find((x) => x.field === f)?.label || f).join(', ')}</b>.
        </Alert>
      )}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap',
                    borderTop: '1px solid var(--border)', paddingTop: 10 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 180 }}>
          <span style={LBL}>Verzió-címke (opcionális)</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="pl. 2026-09 új kulcs"
                 style={BOX} autoComplete="off"/>
        </label>
        <Btn size="sm" icon={KeyRound} onClick={onSave} loading={saveMut.isPending}
             disabled={!isAdmin || saveMut.isPending || !filled.length}
             title={isAdmin ? undefined : 'Csak cég-adminisztrátor módosíthatja.'}>
          Mentés{filled.length ? ` (${filled.length})` : ''}
        </Btn>
        {filled.length > 0 && (
          <Btn size="sm" variant="ghost" onClick={() => { clearDraft(); setReplacing({}); }}><X size={13}/> Bevitel elvetése</Btn>
        )}
        <Btn size="sm" variant="ghost" onClick={() => setShowHistory((s) => !s)}>
          <History size={13}/> {showHistory ? 'Történet elrejtése' : 'Verzió-történet'}
        </Btn>
      </div>
      {whyText && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{whyText}</div>}
      {showHistory && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
          <div style={{ ...LBL, marginBottom: 6 }}>Verzió-történet — {st.service}</div>
          {!hist?.rows?.length ? (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Még nincs rögzített verzió.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <thead><tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                  <th style={{ padding: '4px 8px' }}>Mező</th><th style={{ padding: '4px 8px' }}>Állapot</th>
                  <th style={{ padding: '4px 8px' }}>Létrehozva</th><th style={{ padding: '4px 8px' }}>Leváltva</th>
                  <th style={{ padding: '4px 8px' }}>Címke</th>
                </tr></thead>
                <tbody>
                  {hist.rows.map((r) => (
                    <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '4px 8px' }}>{fields.find((x) => x.field === r.field)?.label || r.field}</td>
                      <td style={{ padding: '4px 8px' }}>{r.is_current ? <Badge variant="success">aktuális</Badge> : <Badge variant="default">korábbi</Badge>}</td>
                      <td style={{ padding: '4px 8px' }}>{fmtTs(r.created_at)}</td>
                      <td style={{ padding: '4px 8px' }}>{fmtTs(r.superseded_at)}</td>
                      <td style={{ padding: '4px 8px' }}>{r.label || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
